import { animate, useMotionValue, useMotionValueEvent, useReducedMotion } from 'motion/react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { cn } from '../../lib/cn.js';

/**
 * Caixa de seleção com mola — só a parte visual.
 *
 * Adaptada do "Spring Check" do reactbits. Uma mola só move as três coisas:
 * o preenchimento laranja cresce do centro e passa um pouco do tamanho (é o
 * que faz a caixa inchar por um instante), o visto se desenha do início ao
 * fim, e tudo volta junto quando desmarca. Com a opção redonda, o visto é um
 * ponto que cresce.
 *
 * Não tem comportamento: quem decide o estado é o `<input>` nativo da
 * `Checkbox`, o botão da opção do seletor ou o da lista de pessoas. Isto aqui
 * só recebe `checked` e desenha — e por isso serve aos três sem duplicar nada.
 *
 * Quem pediu menos movimento recebe a troca seca de estado.
 */

/** Tempo até a mola "chegar", em segundos, antes do quique. */
const VISUAL_DURATION = 0.2;
/** Quanto a caixa incha quando a mola passa de 1. */
const SWELL = 0.35;
/** Quique da referência, já na escala do motion. */
const BOUNCE = 0.54;

export function SpringCheck({
  checked,
  shape = 'square',
  size = 18,
  className,
}: {
  checked: boolean;
  shape?: 'square' | 'round' | undefined;
  size?: number | undefined;
  className?: string | undefined;
}) {
  const reduce = useReducedMotion();
  const t = useMotionValue(checked ? 1 : 0);
  const boxRef = useRef<HTMLSpanElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  const tickRef = useRef<SVGPathElement>(null);
  const dotRef = useRef<HTMLSpanElement>(null);

  // Escreve direto no DOM: estado de React por quadro de mola é desperdício.
  const write = (value: number) => {
    const held = Math.min(1, Math.max(0, value));
    if (fillRef.current) fillRef.current.style.transform = `scale(${Math.max(value, 0)})`;
    if (boxRef.current)
      boxRef.current.style.transform = `scale(${1 + SWELL * Math.max(0, value - 1)})`;
    if (tickRef.current) tickRef.current.style.strokeDashoffset = String(1 - held);
    if (dotRef.current) dotRef.current.style.transform = `scale(${held})`;
  };
  useMotionValueEvent(t, 'change', write);
  useLayoutEffect(() => {
    write(t.get());
  });

  useEffect(() => {
    const target = checked ? 1 : 0;
    if (reduce) {
      t.jump(target);
      return undefined;
    }
    if (t.get() === target && t.getVelocity() === 0) return undefined;
    const controls = animate(t, target, {
      type: 'spring',
      visualDuration: VISUAL_DURATION,
      bounce: BOUNCE,
    });
    return () => controls.stop();
  }, [checked, reduce, t]);

  return (
    <span
      ref={boxRef}
      aria-hidden
      className={cn(
        // Filete de 1,5px e canto de 4px: um fio a mais que o padrão, para a caixa não ler como quadrado seco.
        'relative grid shrink-0 place-items-center overflow-hidden border-[1.5px] transition-colors duration-100',
        shape === 'round' ? 'rounded-full' : 'rounded-[4px]',
        checked ? 'border-primary' : 'border-line-control bg-surface',
        className,
      )}
      style={{ width: size, height: size }}
    >
      {/*
        O preenchimento é maior que a caixa e é recortado pelo `overflow-hidden`,
        que segue o canto *interno* do filete. Do tamanho exato da caixa, com o
        raio de fora, sobravam quatro frestas nos cantos: o raio de dentro da
        borda é menor que o de fora.
      */}
      <span
        ref={fillRef}
        className="absolute -inset-0.5 origin-center rounded-[inherit] bg-primary"
      />
      {shape === 'round' ? (
        <span
          ref={dotRef}
          className="relative rounded-full bg-on-primary"
          style={{ width: size / 3, height: size / 3 }}
        />
      ) : (
        <svg
          viewBox="0 0 24 24"
          className="relative text-on-primary"
          style={{ width: size * 0.72, height: size * 0.72 }}
        >
          <path
            ref={tickRef}
            d="M5 12.5l4.5 4.5L19 7"
            pathLength={1}
            strokeDasharray={1}
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}
